import type { ReactNode } from 'react';
import { useBrand } from '../shared/auth.js';
import { BackLink } from '../ui/PageHeader.js';
import { PlatformLogo, PlatformTrack } from './PlatformMark.js';
import { PLATFORM_LEGAL_LINKS, usePlatformIdentity } from './platform.js';

const EYEBROW = 'Le logiciel de suivi d’atelier';
const SUPPORTING = 'Reportage photo, travaux complémentaires, échanges avec le client : un seul dossier par véhicule.';

/** Nom du produit : réserve sa place pendant le chargement (rien ne saute), jamais de nom par défaut. */
function ProductName({ name, className }: { name: string | null; className?: string }) {
  if (name === null) return <span aria-hidden className={`inline-block w-28 ${className ?? ''}`} />;
  if (!name) return null;
  return <span className={className}>{name}</span>;
}

/**
 * Gabarit du portail global de la plateforme (avant de savoir quel garage se connecte) : connexion,
 * mot de passe oublié, réinitialisation, invitation, choix du garage, démonstration, erreurs publiques.
 * Direction « Rouge Pulse / Contraste » (05/10/2026, docs/DESIGN.md §6 bis).
 *
 * - Ordinateur (≥ 1 024 px) : panneau anthracite à gauche (logo, surtitre, grand titre Barlow avec le
 *   mot d'accent en rouge, phrase vraie sur le produit), ligne rouge à trois étapes sur sa limite ;
 *   formulaire à droite sur fond blanc.
 * - Téléphone : bandeau anthracite de 64 px (logo) puis **le formulaire d'abord** ; rien au-dessus du
 *   titre qui pousse les champs sous le clavier.
 *
 * Aucune marque de garage ici : la présentation neutre est rétablie (`useBrand(null)`) et la portée
 * `platform-scope` donne le bouton rouge et le focus de la plateforme. Le nom vient de `public.platform`.
 */
export function PublicShell({
  title,
  intro,
  back,
  note,
  children,
}: {
  title: string;
  intro?: ReactNode;
  back?: { to: string; label: string };
  /** Mention discrète sous le contenu (ex. : rappel pour les clients sur la connexion). */
  note?: ReactNode;
  children?: ReactNode;
}) {
  // Pages de l'équipe (connexion, invitation, démonstration, erreurs) : face garage.
  const { name, suffix } = usePlatformIdentity();
  useBrand(null, name ? `${title} · ${name}` : title);

  return (
    <div className="platform-scope min-h-dvh bg-surface text-ink lg:grid lg:grid-cols-[minmax(28rem,5fr)_7fr]">
      <div className="platform-panel relative z-10 hidden px-12 py-10 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col xl:px-16 xl:py-12">
        <PlatformLogo name={name} suffix={suffix} tone="dark" width={220} />
        <div className="mt-auto max-w-[30rem] pb-[6vh]">
          <p className="mb-5 text-[0.6875rem] font-semibold tracking-[0.25em] text-on-platform-2 uppercase">{EYEBROW}</p>
          <p className="platform-title text-[3.25rem] text-on-platform xl:text-[4.25rem]">
            <span className="whitespace-nowrap">Le suivi d’atelier,</span>{' '}
            <span className="whitespace-nowrap text-platform-accent-on-panel">enfin clair.</span>
          </p>
          <p className="mt-6 max-w-[36ch] text-[1.0625rem] leading-relaxed text-on-platform-2">{SUPPORTING}</p>
        </div>
        <PlatformTrack className="top-0 -right-[1.5px] bottom-[10%]" />
      </div>

      <div className="flex min-h-dvh flex-col">
        <header className="platform-panel flex h-16 shrink-0 items-center border-b-[3px] border-platform-accent-on-panel px-[var(--page-x)] lg:hidden">
          <PlatformLogo name={name} suffix={suffix} tone="dark" width={156} />
        </header>

        <main className="flex flex-1 flex-col px-[var(--page-x)] pt-6 pb-10 sm:items-center sm:justify-center sm:pt-10 lg:px-16 lg:py-16">
          <div className="w-full sm:max-w-[25rem]">
            {back ? <BackLink to={back.to} label={back.label} className="mb-3" /> : null}
            <h1 className="platform-title text-[2.25rem] text-balance text-ink sm:text-[2.75rem]">{title}</h1>
            {intro ? <div className="mt-2.5 text-base leading-relaxed text-ink-2">{intro}</div> : null}
            {children ? <div className="mt-6">{children}</div> : null}
            {note ? <div className="mt-6 border-t border-line pt-5 text-sm leading-relaxed text-ink-3">{note}</div> : null}
          </div>
        </main>

        <footer className="px-[var(--page-x)] pt-2 pb-[calc(0.75rem+var(--safe-bottom))] lg:px-16 lg:pb-6">
          <div className="flex flex-col items-center gap-x-6 text-sm text-ink-3 sm:flex-row sm:justify-between">
            <ProductName name={name} className="font-medium text-ink-2" />
            <nav aria-label="Informations légales">
              <ul className="flex items-center gap-x-1">
                {PLATFORM_LEGAL_LINKS.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      className="inline-flex min-h-(--ctl-sm) items-center rounded-lg px-2.5 underline decoration-line-strong underline-offset-4 transition-colors hover:text-ink hover:decoration-ink-3 focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </footer>
      </div>
    </div>
  );
}
